<template>
  <div>
    <Loading v-if="isLoading" :type="2" />
    <div v-else>
      <div v-bind:style="{ color: this.getDifferenceColor() }">
        <i class="chart line icon" />{{ differenceData }}%
        {{ "components.charts.clientNotification.graphDifference" | i18n }}
      </div>
      <h4 class="font-weight-normal">
        {{ "components.charts.clientNotification.graphTitle" | i18n }}
      </h4>
      <h6 class="text-light font-weight-light pb-2">
        {{ "components.charts.clientNotification.graphSubTitle" | i18n }}
      </h6>
      <canvas
        ref="graphCanvas"
        id="graphicCanvas"
        class="canvas w-100 h-100"
      ></canvas>
      <div
        class="overlay d-flex position-absolute font-weight-light justify-content-center align-items-center text-white h-100 w-100"
        v-if="dataEmpty"
        :style="{ 'border-radius': calculateRadius() }"
      >
        <div class="text-center">
          <div>
            <i class="icon exclamation circle"></i>
          </div>
          <div>
            {{ "components.charts.noDataOverlay" | i18n }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "../others/Loading.vue";
import { Chart } from "chart.js";
/**
 * @description Composante qui génère un graphique des notifications envoyées dans la semaine passée pour un client
 * @author Philippe Ménard
 * @vue-prop {Number} clientId - L'id du client ou -1 pour tous les clients
 * @vue-data {Array} weekDays - Les jours de la semaine
 * @vue-data {Array} formatedWeek - Les jours de la semaine dans l'ordre pour le graphique
 * @vue-data {Object} chart - Le graphique
 * @vue-data {Number} differenceData - La différence d'envoi depuis la dernière semaine
 * @vue-data {Boolean} dataEmpty - Si les données sont vides
 * @vue-data {Boolean} isLoading - Si le chargement des données est en cours
 */
export default {
  name: "ChartNotificationsByClient",
  components: {
    Loading
  },
  props: {
    clientId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      weekDays: [
        "Dimanche",
        "Lundi",
        "Mardi",
        "Mercredi",
        "Jeudi",
        "Vendredi",
        "Samedi"
      ],
      formatedWeek: [],
      chart: null,
      differenceData: 0,
      dataEmpty: false,
      isLoading: true
    };
  },
  methods: {
    /**
     * @description Obtient la valeur du border-radius à utiliser afin de s'adapter à la page
     * @returns {String} La valeur du border-radius
     */
    calculateRadius() {
      return this.clientId === -1 ? "25px" : "15px";
    },
    /**
     * @description Génère les valeurs pour remplir le graphique
     * @returns {Array} Les valeurs pour remplir le graphique
     */
    async generateDatasets() {
      let datasets = [];
      let data = await this.getChartData();
      this.dataEmpty =
        data[0].reduce((a, b) => a + b, 0) +
          data[1].reduce((a, b) => a + b, 0) ===
        0;

      let labels;
      try {
        labels = [
          this.$i18n.t("components.charts.clientNotification.labels.email"),
          this.$i18n.t(
            "components.charts.clientNotification.labels.textMessage"
          )
        ];
      } catch (e) {
        if (typeof e != TypeError) {
          throw e;
        }
      }
      let colors = [
        getComputedStyle(document.body).getPropertyValue("--danger"),
        getComputedStyle(document.body).getPropertyValue("--blue")
      ];
      for (let i = 0; i < 2; i++) {
        datasets.push({
          data: data[i],
          label: labels[i],
          backgroundColor: colors[i],
          hoverBackgroundColor: colors[i]
        });
      }
      this.isLoading = false;
      return datasets;
    },
    /**
     * @description Gère l'obtention de données de l'api
     * @returns {Array} Les emails et textos envoyés. La dernière valeur est la plus récente
     */
    async getChartData() {
      let res;
      if (this.clientId === -1) {
        res = await this.$api.getNotificationStatsAllClients();
      } else {
        res = await this.$api.getClientNotificationLastWeek(this.clientId);
      }
      if (res.data.meta !== undefined && res.data.meta !== null) {
        this.differenceData = res.data.meta;
      } else {
        this.differenceData = 0;
      }
      let emails = res.data.emails.slice().reverse();
      let textMessages = res.data.textos.slice().reverse();
      return [emails, textMessages];
    },
    /**
     * @description Prépare les données pour le graphique
     */
    getData() {
      this.formatWeekDays();
      this.generateDatasets();
    },
    /**
     * @description Obtient la bonne couleur pour la différence d'envoi de notifications
     * @returns {String} la couleur pour la différence d'envoi
     */
    getDifferenceColor() {
      if (this.differenceData > 0) {
        return "red";
      } else if (this.differenceData === 0) {
        return "black";
      } else {
        return "green";
      }
    },
    /**
     * @description Obtient la date pour chacune des entrées sur le graphique
     */
    formatWeekDays() {
      let date = new Date();
      this.formatedWeek = [];

      for (let index = 0; index < 7; index++) {
        let dateCopy = new Date();
        dateCopy.setDate(date.getDate() - index);
        this.formatedWeek[6 - index] = this.weekDays[dateCopy.getDay()];
      }
    },
    /**
     * @description Fonction qui génère le graphique dans le canvas
     */
    async generateChart() {
      const datasets = await this.generateDatasets();
      this.chart = new Chart(this.$refs.graphCanvas, {
        responsive: true,
        type: "bar",
        data: {
          labels: this.formatedWeek,
          datasets: datasets
        },
        options: {
          legend: {
            display: window.innerWidth > 414
          },
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                  precision: 0
                }
              }
            ]
          }
        }
      });
    }
  },
  /**
   * @description Permet d'obtenir les données nécessaires au graphique
   */
  beforeMount() {
    this.getData();
  },
  /**
   * @description Génère le graphique lorsque la page se charge
   */
  mounted() {
    this.generateChart();
  }
};
</script>

<style lang="scss" scoped>
.overlay {
  font-size: 1.25em;
  background: rgba(0, 0, 0, 0.35);
  top: 0;
  left: 0;
}
</style>
